// quarterly_view.jsx
import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { DocumentTextIcon, UserMinusIcon, PencilSquareIcon } from "@heroicons/react/24/outline";

const DOC_COLOR = { bg: "#e0e7ff", border: "#6366f1" };
const CTR_COLOR = { bg: "#fef3c7", border: "#f59e0b" };
const SIGNED_DOC_COLOR = { bg: "#dcfce7", border: "#16a34a" };

// 2 righe x 2 colonne = 4 pillole max visibili
const MAX_ITEMS_PER_DAY = 4;

export default function QuarterlyView({
    baseDate,
    events = [],
    onItemDrop,   // ({ kind, entityId, employeeId, startDate, targetYmd }) => Promise/void
    onItemClick,  // ({ kind, id, raw, employeeId }) => void
}) {
    const [openDay, setOpenDay] = useState(null); // ymd del giorno aperto nel popover

    const firstMonth = useMemo(
        () => dayjs(baseDate).startOf("month"),
        [baseDate]
    );

    const months = useMemo(
        () => [
            firstMonth,
            firstMonth.add(1, "month"),
            firstMonth.add(2, "month"),
        ],
        [firstMonth]
    );

    const maxDays = useMemo(
        () => Math.max(...months.map((m) => m.daysInMonth())),
        [months]
    );

    // mappa YYYY-MM-DD -> [eventi...] solo per il trimestre
    const eventsByDate = useMemo(() => {
        const start = firstMonth.startOf("month");
        const end = firstMonth.add(2, "month").endOf("month");
        const map = {};

        (events || []).forEach((ev) => {
        const ymd = dayjs(ev.start).format("YYYY-MM-DD");
        const d = dayjs(ymd);
        if (!d.isBetween(start, end, "day", "[]")) return;

        if (!map[ymd]) map[ymd] = [];
        map[ymd].push(ev);
        });
        return map;
    }, [events, firstMonth]);

    const renderPill = (ev, { compact = true, fullWidth = false } = {}) => {
        const kind = ev.extendedProps?.kind;
        const Icon = kind === "contract" ? UserMinusIcon : kind === "signed_document" ? PencilSquareIcon : DocumentTextIcon;
        const fullTitle = ev.title || "";
        const maxChars = compact ? 18 : 40;
        const shortTitle =
            fullTitle.length > maxChars ? `${fullTitle.slice(0, maxChars)}…` : fullTitle;

        const borderColorClass = kind === "contract" ? "border-amber-500" : kind === "signed_document" ? "border-green-500" : "border-indigo-500";
        const bgColorClass = kind === "contract" ? "bg-amber-50" : kind === "signed_document" ? "bg-green-50" : "bg-indigo-50";

        const payload = JSON.stringify({
            kind,
            entityId: ev.extendedProps?.entityId,
            employeeId: ev.extendedProps?.employeeId ?? null,
            startDate: ev.extendedProps?.startDate ?? null,
        });

        const handleClick = () =>
            onItemClick?.({
                kind,
                id: ev.extendedProps?.entityId,
                raw: ev.extendedProps?.raw,
                employeeId: ev.extendedProps?.employeeId ?? null,
            });

        const baseClasses = compact
            ? "flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-semibold cursor-pointer border"
            : "flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-left border";

        return (
            <button
                key={ev.id + (compact ? "-c" : "-f")}
                type="button"
                title={fullTitle}
                draggable
                onDragStart={(e) =>
                    e.dataTransfer.setData("text/plain", payload)
                }
                onClick={handleClick}
                className={`${baseClasses} ${borderColorClass} ${bgColorClass} ${
                    fullWidth ? "w-full justify-start" : ""
                }`}
            >
                <Icon
                    className={
                    compact ? "h-2.5 w-2.5 flex-none" : "h-3 w-3 flex-none"
                    }
                />
                <span
                    className={
                    compact
                        ? "truncate max-w-[6rem]"
                        : "truncate flex-1 min-w-0 leading-4"
                    }
                >
                    {shortTitle}
                </span>
            </button>
        );
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {months.map((m) => {
                const monthKey = m.format("YYYY-MM");
                const daysInMonth = m.daysInMonth();

                return (
                    <div
                        key={monthKey}
                        className="border border-gray-200 bg-white shadow-sm overflow-hidden"
                    >
                        {/* header mese */}
                        <div className="px-3 py-2 text-center font-normal uppercase border-b bg-indigo-50" style={{  letterSpacing: '0.03em', fontWeight: '500', color: '#6d28d9', fontSize: '14px',}}>
                            {m.locale("it").format("MMMM YYYY")}
                        </div>

                        {/* righe: 1..maxDays, ogni riga = giorno */}
                        <div>
                            {Array.from({ length: maxDays }).map((_, idx) => {
                                const dayNum = idx + 1;

                                if (dayNum > daysInMonth) {
                                return (
                                    <div
                                        key={`${monthKey}-empty-${dayNum}`}
                                        className="h-7 sm:h-8 border-b border-gray-100 bg-gray-50/40"
                                    />
                                );
                                }

                                const d = m.date(dayNum);
                                const ymd = d.format("YYYY-MM-DD");
                                const isToday = d.isSame(dayjs(), "day");
                                const label = d.locale("it").format("D ddd");
                                const list = eventsByDate[ymd] || [];

                                const hasDoc = list.some((ev) => ev.extendedProps?.kind === "document");
                                const hasCtr = list.some((ev) => ev.extendedProps?.kind === "contract");
                                const hasSignedDoc = list.some((ev) => ev.extendedProps?.kind === "signed_document");

                                // max 4 elementi visibili = 2 righe in griglia 2x2
                                const visible = list.slice(0, MAX_ITEMS_PER_DAY);
                                const hiddenCount = list.length - visible.length;

                                // popover sopra/ sotto
                                const openAbove = idx > maxDays - 4;

                                const handleDrop = async (e) => {
                                    e.preventDefault();
                                    if (!onItemDrop) return;

                                    const data = e.dataTransfer.getData("text/plain");
                                    if (!data) return;
                                    let payload;
                                    try {
                                        payload = JSON.parse(data);
                                    } catch {
                                        return;
                                    }

                                    onItemDrop({
                                        ...payload,
                                        targetYmd: ymd,
                                    });
                                };


                                return (
                                    <div key={ymd} className="relative">
                                        <div
                                            className={`min-h-[2.4rem] border-b px-2 py-1 ${
                                                isToday
                                                ? "bg-indigo-50/50 ring-1 ring-inset ring-indigo-200"
                                                : "border-gray-100"
                                            }`}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={handleDrop}
                                        >
                                            <div className="flex items-start gap-2">
                                                {/* colonna sinistra: giorno + icone */}
                                                <div className="flex flex-col items-start mt-0.5">
                                                    <span className="text-[11px] text-gray-600 whitespace-nowrap uppercase">
                                                        {label}
                                                    </span>

                                                    {(hasDoc || hasCtr || hasCtr) && (
                                                        <div className="mt-0.5 flex items-center gap-0.5">
                                                            {hasDoc && (
                                                                <span
                                                                    className="inline-flex h-4 w-4 items-center justify-center rounded-full"
                                                                    style={{
                                                                        backgroundColor: DOC_COLOR.bg,
                                                                        color: DOC_COLOR.border,
                                                                    }}
                                                                >
                                                                    <DocumentTextIcon className="h-3.5 w-3.5" />
                                                                </span>
                                                            )}
                                                            {hasSignedDoc && (
                                                                <span
                                                                    className="inline-flex h-4 w-4 items-center justify-center rounded-full"
                                                                    style={{
                                                                        backgroundColor: SIGNED_DOC_COLOR.bg,
                                                                        color: SIGNED_DOC_COLOR.border,
                                                                    }}
                                                                    title="Documenti con firma in scadenza"
                                                                >
                                                                    <PencilSquareIcon className="h-3.5 w-3.5" />
                                                                </span>
                                                            )}
                                                            {hasCtr && (
                                                                <span
                                                                    className="inline-flex h-4 w-4 items-center justify-center rounded-full"
                                                                    style={{
                                                                        backgroundColor: CTR_COLOR.bg,
                                                                        color: CTR_COLOR.border,
                                                                    }}
                                                                >
                                                                    <UserMinusIcon className="h-3.5 w-3.5" />
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* colonna destra: pillole eventi */}
                                                <div className="flex-1">
                                                    {/* 2 colonne → max 2 righe (4 elementi) */}
                                                    <div className="grid grid-cols-2 gap-1">
                                                        {visible.map((ev, idxVis) => {
                                                            const row = Math.floor(idxVis / 2) + 1;          // riga 1 o 2
                                                            const col = idxVis % 2 === 0 ? 2 : 1;            // pari -> col 2, dispari -> col 1

                                                            return (
                                                                <div
                                                                    key={ev.id}
                                                                    className="col-span-1"
                                                                    style={{ gridColumnStart: col, gridRowStart: row }}
                                                                >
                                                                    {renderPill(ev, { compact: true, fullWidth: true })}
                                                                </div>
                                                            );
                                                        })}

                                                        {hiddenCount > 0 && (
                                                            <button
                                                                type="button"
                                                                className="col-span-2 text-[11px] text-gray-700 font-semibold text-left mt-0.5"
                                                                onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenDay((prev) => (prev === ymd ? null : ymd));
                                                                }}
                                                            >
                                                                +altri {hiddenCount}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Popover con tutti gli eventi del giorno */}
                                        {openDay === ymd && list.length > 0 && (
                                            <div
                                                className={`
                                                absolute z-50 w-64 rounded-lg border border-gray-300 bg-white shadow-xl
                                                ${openAbove ? "bottom-full mb-2" : "top-full mt-2"}
                                                `}
                                                style={{ right: 0 }}
                                            >
                                                <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-100">
                                                    <span className="text-xs font-semibold uppercase">
                                                        {d.locale("it").format("D MMMM YYYY")}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="text-xs text-gray-400 hover:text-gray-600"
                                                        onClick={() => setOpenDay(null)}
                                                    >
                                                        ×
                                                    </button>
                                                </div>

                                                <div className="max-h-64 overflow-auto px-2 py-2 space-y-1">
                                                    {list.map((ev) => renderPill(ev, { compact: false }))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}